"use client";

import { useState } from "react";
import {
  Shield,
  AlertTriangle,
  Globe,
  Calendar,
  ChevronDown,
  ChevronUp,
  Zap,
  Target,
  Clock,
} from "lucide-react";
import { ContextAnalysis, GeopoliticsData } from "@/hooks/useAIContextAnalysis";

interface GeopoliticsAISidebarProps {
  analysis: ContextAnalysis;
  eventTitle: string;
}

const escalationConfig: Record<string, { color: string; bg: string; text: string; label: string; width: string }> = {
  low: { color: "bg-emerald-500", bg: "bg-emerald-500/10", text: "text-emerald-500", label: "Low", width: "w-1/4" },
  medium: { color: "bg-amber-500", bg: "bg-amber-500/10", text: "text-amber-500", label: "Medium", width: "w-2/4" },
  high: { color: "bg-orange-500", bg: "bg-orange-500/10", text: "text-orange-500", label: "High", width: "w-3/4" },
  critical: { color: "bg-red-500", bg: "bg-red-500/10", text: "text-red-500", label: "Critical", width: "w-full" },
};

const roleConfig: Record<string, { bg: string; text: string }> = {
  aggressor: { bg: "bg-red-500/10", text: "text-red-500" },
  target: { bg: "bg-orange-500/10", text: "text-orange-500" },
  mediator: { bg: "bg-blue-500/10", text: "text-blue-500" },
  ally: { bg: "bg-violet-500/10", text: "text-violet-500" },
  neutral: { bg: "bg-gray-500/10", text: "text-gray-500" },
};

const conflictLabels: Record<string, string> = {
  military_strike: "Military Strike",
  sanctions: "Sanctions",
  diplomacy: "Diplomacy",
  territorial: "Territorial Dispute",
  cyber: "Cyber Conflict",
  economic: "Economic / Trade War",
  proxy_war: "Proxy War",
};

export default function GeopoliticsAISidebar({
  analysis,
  eventTitle,
}: GeopoliticsAISidebarProps) {
  const [showAllEvents, setShowAllEvents] = useState(false);

  const geo = analysis.geopoliticsData;
  if (!geo) return null;

  const escalation = escalationConfig[geo.escalationLevel] || escalationConfig.medium;
  const parties = geo.involvedParties || [];
  const recentEvents = geo.recentEvents || [];
  const displayEvents = showAllEvents ? recentEvents : recentEvents.slice(0, 4);
  const hasMoreEvents = recentEvents.length > 4;
  const timeline = geo.timeline || [];
  const concerns = geo.keyConcerns || [];

  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 dark:border-[#1A1A1A]">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-[5px] bg-red-500/10 flex items-center justify-center">
            <Shield className="w-4 h-4 text-red-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-white truncate">
              {analysis.widgetConfig.title || "Geopolitical Context"}
            </h3>
            <span className="text-[10px] text-gray-400">
              {conflictLabels[geo.conflictType] || geo.conflictType}
            </span>
          </div>
        </div>

        {/* Escalation gauge */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-medium text-gray-400">Escalation Level</span>
            <span className={`text-[10px] font-bold ${escalation.text}`}>
              {escalation.label}
            </span>
          </div>
          <div className="h-1.5 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px] overflow-hidden">
            <div className={`h-full rounded-[5px] ${escalation.color} transition-all duration-500`} style={{
              width: geo.escalationLevel === "low" ? "25%" : geo.escalationLevel === "medium" ? "50%" : geo.escalationLevel === "high" ? "75%" : "100%",
            }} />
          </div>
        </div>
      </div>

      <div className="p-4 space-y-5">
        {/* Involved Parties */}
        {parties.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-3">
              <Globe className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                Involved Parties
              </span>
            </div>
            <div className="space-y-2">
              {parties.map((party, idx) => {
                const role = roleConfig[party.role] || roleConfig.neutral;
                const flagUrl = party.flagCode
                  ? `https://flagcdn.com/w80/${party.flagCode.toLowerCase()}.png`
                  : null;

                return (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-2.5 rounded-[5px] bg-gray-50 dark:bg-white/[0.02]"
                  >
                    {/* Flag */}
                    <div className="w-9 h-7 rounded overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-[#0f0f12]">
                      {flagUrl ? (
                        <img
                          src={flagUrl}
                          alt={party.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Globe className="w-3.5 h-3.5 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-medium text-gray-900 dark:text-white truncate">
                        {party.name}
                      </p>
                      {party.description && (
                        <p className="text-[10px] text-gray-400 truncate mt-0.5">
                          {party.description}
                        </p>
                      )}
                    </div>

                    {/* Role pill */}
                    <span className={`px-2 py-0.5 rounded-[5px] text-[9px] font-bold capitalize flex-shrink-0 ${role.bg} ${role.text}`}>
                      {party.role}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Events — vertical timeline */}
        {recentEvents.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-3">
              <Target className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                Recent Events
              </span>
            </div>
            <div className="relative pl-4">
              {/* Vertical line */}
              <div className="absolute left-[5px] top-1 bottom-1 w-px bg-gray-200 dark:bg-[#0f0f12]" />

              <div className="space-y-3">
                {displayEvents.map((event, idx) => (
                  <div key={idx} className="relative">
                    {/* Dot on timeline */}
                    <span
                      className={`absolute -left-4 top-[5px] w-[7px] h-[7px] rounded-[5px] ring-2 ring-white dark:ring-[#18181D] ${
                        event.significance === "high"
                          ? "bg-red-500"
                          : event.significance === "medium"
                            ? "bg-amber-500"
                            : "bg-gray-300 dark:bg-gray-600"
                      }`}
                    />
                    <div>
                      <p className="text-[12px] text-gray-700 dark:text-gray-300 leading-relaxed">
                        {event.event}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" />
                        {event.date}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {hasMoreEvents && (
              <button
                onClick={() => setShowAllEvents(!showAllEvents)}
                className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mt-2 ml-4 transition-colors"
              >
                {showAllEvents ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {showAllEvents ? "Show less" : `${recentEvents.length - 4} more events`}
              </button>
            )}
          </div>
        )}

        {/* Key Concerns */}
        {concerns.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-3">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                Key Concerns
              </span>
            </div>
            <div className="space-y-2">
              {concerns.slice(0, 5).map((concern, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <span className="w-1 h-1 rounded-[5px] bg-amber-400 mt-[6px] flex-shrink-0" />
                  <p className="text-[12px] text-gray-600 dark:text-gray-400 leading-relaxed">
                    {concern}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timeline */}
        {timeline.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-3">
              <Calendar className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                Timeline
              </span>
            </div>
            <div className="space-y-1.5">
              {timeline.slice(0, 6).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-1">
                  <span
                    className={`text-[12px] flex-1 min-w-0 truncate ${
                      item.isPast
                        ? "text-gray-400 line-through decoration-gray-300 dark:decoration-gray-600"
                        : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {item.event}
                  </span>
                  <span
                    className={`text-[10px] tabular-nums ml-3 flex-shrink-0 ${
                      item.isPast
                        ? "text-gray-300 dark:text-gray-600"
                        : "font-medium text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    {item.date}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Historical Note */}
        {geo.historicalNote && (
          <div className="bg-gray-50 dark:bg-white/[0.02] rounded-[5px] p-3">
            <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
              {geo.historicalNote}
            </p>
          </div>
        )}

        {/* AI Reasoning */}
        {analysis.reasoning && (
          <details className="group">
            <summary className="flex items-center gap-1.5 text-[11px] text-gray-400 cursor-pointer hover:text-gray-500 dark:hover:text-gray-300 transition-colors list-none">
              <Zap className="w-3 h-3" />
              <span>AI Reasoning</span>
              <ChevronDown className="w-3 h-3 ml-auto group-open:rotate-180 transition-transform" />
            </summary>
            <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
              {analysis.reasoning}
            </p>
          </details>
        )}
      </div>
    </div>
  );
}
